// src/components/forms/fields/AppointmentTimeField.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight, Info, Loader2, User, Video, X } from 'lucide-react';
import { apiService as sharedApiService } from '../../../services/api';
import { FormDefinition, PreferredSlot, AppointmentTimeData } from '@prism-ehr/shared';
import { resolveDaySchedule } from '../../../utils/scheduleTemplateResolver';
import { buildHolidaySet } from '../../../utils/holidayUtils';

const MAX_PREFERRED_SLOTS = 3;

interface SchedulingApiService {
    getOrganizationPublic: (organizationId: string) => Promise<any>;
    getOrganizationUsersPublic: (organizationId: string) => Promise<any[]>;
    getAppointmentTypesPublic: (organizationId: string, options?: { locationId?: string }) => Promise<any[]>;
    getProviderAvailabilityPublic: (providerIds: string, options?: { locationId?: string; organizationId?: string }) => Promise<any>;
    getScheduleOverridesPublic: (providerIds: string, startDate: string, endDate: string, options?: { organizationId?: string }) => Promise<any[]>;
    getAppointmentsPublic: (data: { provider_id: string; organization_id?: string; date_range: { start: string; end: string }; location_id?: string }) => Promise<any[]>;
    getUserPublic: (userId: string) => Promise<any>;
}

interface AppointmentType {
    id: string;
    name: string;
    duration_minutes: number;
    color?: string;
    allow_online_booking: boolean;
    allow_portal_booking?: boolean;
    telehealth_status?: 'accepted' | 'required' | null;
    provider_ids?: string[];
}

interface DayInfo {
    date: Date;
    isCurrentMonth: boolean;
    isPast: boolean;
    isTooFar: boolean;
    isToday: boolean;
    isSelected: boolean | null;
    isDisabled: boolean;
}

interface OrganizationSettings {
    scheduling_mode?: 'auto_confirm' | 'require_approval';
    allow_time_adjustment?: boolean;
    default_duration_minutes?: number;
    advance_booking_days?: number;
    minimum_notice_hours?: number;
    company_holidays?: Array<{ date: string; name: string }>;
    /** Org setting: calendar notes (schedule_override type 'note') hide online slots. Absent/false = notes ignored (current behavior). */
    self_service_notes_block_online?: boolean;
}

interface ProviderSlot {
    provider_id: string;
    provider_name: string;
    date: string;
    start_time: string;
    end_time: string;
    available: boolean;
}

interface AppointmentTimeFieldProps {
    form: FormDefinition;
    providerId?: string;
    locationId?: string | null;
    value: AppointmentTimeData | null;
    onChange: (value: AppointmentTimeData) => void;
    showAppointmentTypes?: boolean;
    showProviderName?: boolean;
    required?: boolean;
    disabled?: boolean;
    forceSingleSlot?: boolean;
    providerName?: string;
    apiService?: SchedulingApiService;
    bookingContext?: 'online' | 'portal' | 'provider';
    /** Multi-location orgs: block slot fetching/display until a location is chosen. */
    requireLocation?: boolean;
    /** When no providerId is set, restrict the any-provider search to these ids. */
    providerIds?: string[];
    /** Fixed appointment type (used when showAppointmentTypes is false). */
    appointmentType?: { id: string; name?: string; duration_minutes: number; provider_ids?: string[] };
    /**
     * Look-ahead scan (2026-10-21): once per selection change, range-fetch the
     * providers' availability / overrides / appointments from today through
     * Saturday of next week and show a notice when no slot exists. Same request
     * count as one day's load.
     */
    scanAheadEnabled?: boolean;
    /** Shown as a "Join the waitlist" button inside the no-availability notice. */
    onJoinWaitlist?: () => void;
}

const detectTimezone = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
        console.error('Failed to detect timezone:', error);
        return 'UTC';
    }
};

// Collapse multiple per-provider slots at the same (date, time) into a single slot,
// assigning each collapsed group to the candidate provider with the lowest running
// load. Counts are mutated in-place so subsequent groups balance against earlier picks.
function condenseSlotsRoundRobin(
    slots: any[],
    counts: Record<string, number>
): any[] {
    const groups = new Map<string, any[]>();
    for (const slot of slots) {
        const key = `${slot.date}|${slot.start_time}`;
        const arr = groups.get(key);
        if (arr) arr.push(slot); else groups.set(key, [slot]);
    }
    const result: any[] = [];
    for (const group of groups.values()) {
        if (group.length === 1) {
            const { provider_name, ...rest } = group[0];
            result.push(rest);
            continue;
        }
        const winner = group.reduce((best, candidate) => {
            const bestLoad = counts[best.provider_id] ?? 0;
            const candLoad = counts[candidate.provider_id] ?? 0;
            return candLoad < bestLoad ? candidate : best;
        });
        counts[winner.provider_id] = (counts[winner.provider_id] ?? 0) + 1;
        const { provider_name, ...rest } = winner;
        result.push(rest);
    }
    return result;
}

const AppointmentTimeField: React.FC<AppointmentTimeFieldProps> = ({
    form,
    providerId,
    locationId,
    value,
    onChange,
    showAppointmentTypes = true,
    showProviderName = true,
    required = false,
    disabled = false,
    forceSingleSlot = false,
    providerName,
    apiService: injectedApiService,
    bookingContext,
    requireLocation = false,
    providerIds,
    appointmentType,
    scanAheadEnabled = false,
    onJoinWaitlist
}) => {
    const apiService = injectedApiService || sharedApiService;
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [orgSettingsLoaded, setOrgSettingsLoaded] = useState(false);
    const [noAvailabilityAhead, setNoAvailabilityAhead] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState<ProviderSlot | null>(null);
    const [selectedSlots, setSelectedSlots] = useState<PreferredSlot[]>([]);
    const [availableSlots, setAvailableSlots] = useState<ProviderSlot[]>([]);
    const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
    const [selectedType, setSelectedType] = useState<AppointmentType | null>(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [orgSettings, setOrgSettings] = useState<OrganizationSettings>({});
    const [providers, setProviders] = useState<any[]>([]);
    const [isTelehealth, setIsTelehealth] = useState(false);

    // Unset scheduling_mode means require_approval — matches the server default in
    // patients/appointment-request and the public form endpoint's auto_confirm gate.
    const isMultiSlotMode = !forceSingleSlot && orgSettings.scheduling_mode !== 'auto_confirm';
    const awaitingLocation = requireLocation && !locationId;
    // Stable key so a caller passing a fresh array each render does not retrigger effects.
    const providerIdsKey = providerIds ? providerIds.join(',') : '';

    // Injected type (types hidden): seed/refresh the selected type from the prop.
    useEffect(() => {
        if (showAppointmentTypes || !appointmentType) return;
        setSelectedType({
            id: appointmentType.id,
            name: appointmentType.name || '',
            duration_minutes: appointmentType.duration_minutes,
            allow_online_booking: true,
            provider_ids: appointmentType.provider_ids
        });
    }, [showAppointmentTypes, appointmentType?.id, appointmentType?.name, appointmentType?.duration_minutes]);

    // Load organization settings on mount
    useEffect(() => {
        loadOrganizationSettings();
    }, [form.organization_id]);

    // Load providers if no provider selected
    useEffect(() => {
        if (!providerId && form.organization_id) {
            loadProviders();
        }
    }, [providerId, form.organization_id]);

    // Load appointment types on mount. Also re-fetch when locationId changes
    // so types restricted to specific locations drop in/out of the list.
    useEffect(() => {
        if (showAppointmentTypes && form.organization_id) {
            loadAppointmentTypes();
        }
    }, [form.organization_id, showAppointmentTypes, locationId]);

    // If the currently-selected type is no longer valid after types reload
    // (e.g., user picked a location that restricts it), reset to the first
    // valid type so the form doesn't submit an invalid appointment_type.
    useEffect(() => {
        if (!showAppointmentTypes) return;
        if (!selectedType) return;
        const stillValid = appointmentTypes.some(t => t.id === selectedType.id);
        if (!stillValid) {
            setSelectedType(appointmentTypes[0] || null);
        }
    }, [appointmentTypes]);

    useEffect(() => {
        if (selectedType?.telehealth_status === 'required') {
            setIsTelehealth(true);
        } else if (selectedType?.telehealth_status !== 'accepted') {
            setIsTelehealth(false);
        }
    }, [selectedType]);

    // Load available slots when date is selected
    useEffect(() => {
        if (awaitingLocation || !orgSettingsLoaded) return;
        if (selectedDate && (providerId || providers.length > 0)) {
            loadAvailableSlots();
        }
    }, [selectedDate, selectedType, providerId, providers, locationId, providerIdsKey, orgSettingsLoaded]);

    // Freshness without polling (2026-10-22): when the tab becomes visible again
    // and the selected day's slots are older than SLOT_STALE_MS, reload that day
    // once. No timers run; a tab left open and untouched for hours makes no
    // requests until the patient actually returns to it.
    const SLOT_STALE_MS = 5 * 60 * 1000;
    const lastSlotsLoadedAtRef = useRef<number>(0);
    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (awaitingLocation || !selectedDate || loading) return;
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            if (lastSlotsLoadedAtRef.current === 0) return;
            if (Date.now() - lastSlotsLoadedAtRef.current < SLOT_STALE_MS) return;
            loadAvailableSlots();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
        // Re-registered on every input change so the listener always calls the
        // current loadAvailableSlots closure.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awaitingLocation, selectedDate, loading, selectedType, providerId, providers, locationId, providerIdsKey]);

    // Look-ahead scan: runs once per selection change (never per day) and only
    // after the org settings and the injected type are in place so the floor
    // and the duration match what the calendar and the day load use.
    useEffect(() => {
        setNoAvailabilityAhead(false);
        if (!scanAheadEnabled || awaitingLocation || !orgSettingsLoaded) return;
        if (!providerId && providers.length === 0) return;
        if (appointmentType && !selectedType) return;
        let cancelled = false;
        (async () => {
            try {
                const found = await scanAheadHasAvailability();
                if (!cancelled) setNoAvailabilityAhead(!found);
            } catch (err) {
                console.error('Error scanning upcoming availability:', err);
                if (!cancelled) setNoAvailabilityAhead(false);
            }
        })();
        return () => { cancelled = true; };
    }, [scanAheadEnabled, awaitingLocation, orgSettingsLoaded, providerId, providers, locationId, providerIdsKey, selectedType?.id]);

    // True as soon as one slot exists for the current selections between the
    // calendar's earliest bookable day and Saturday of next week. One
    // availability + one overrides call for all providers (CSV), one
    // appointments call per provider, all range-bounded; slot math reuses
    // calculateAvailableSlots per day.
    const scanAheadHasAvailability = async (): Promise<boolean> => {
        const duration = selectedType?.duration_minutes ||
            orgSettings.default_duration_minutes ||
            30;

        const minDate = new Date();
        minDate.setHours(minDate.getHours() + (orgSettings.minimum_notice_hours || 24));
        minDate.setHours(0, 0, 0, 0);
        const endDate = new Date();
        endDate.setHours(0, 0, 0, 0);
        endDate.setDate(endDate.getDate() + (13 - endDate.getDay()));
        if (minDate > endDate) return true;

        const days: string[] = [];
        for (let d = new Date(minDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            days.push(d.toISOString().split('T')[0]);
        }
        const afterEnd = new Date(endDate);
        afterEnd.setDate(afterEnd.getDate() + 1);
        const rangeStart = days[0];
        const rangeEnd = afterEnd.toISOString().split('T')[0];

        let providerList: any[];
        if (providerId) {
            providerList = [{ id: providerId }];
        } else {
            providerList = providers.filter((u: any) => u.is_provider);
            if (providerIds && providerIds.length > 0) {
                providerList = providerList.filter((p: any) => providerIds.includes(p.id));
            }
            if (selectedType?.provider_ids?.length) {
                providerList = providerList.filter((p: any) => selectedType.provider_ids!.includes(p.id));
            }
        }
        if (providerList.length === 0) return false;

        const providerIdCsv = providerList.map((p: any) => p.id).join(',');
        const [allAvailability, allOverrides] = await Promise.all([
            apiService.getProviderAvailabilityPublic(providerIdCsv, { locationId: locationId || undefined, organizationId: form.organization_id }),
            apiService.getScheduleOverridesPublic(providerIdCsv, rangeStart, rangeEnd, { organizationId: form.organization_id })
        ]);
        const availabilityList = Array.isArray(allAvailability) ? allAvailability : [allAvailability];
        const overrideList = Array.isArray(allOverrides) ? allOverrides : [];

        for (const provider of providerList) {
            const availability = providerId
                ? (availabilityList[0] || null)
                : availabilityList.find((a: any) => a && a.provider_id === provider.id);
            if (!availability) continue;
            if (!providerId && !availability.is_active) continue;

            const providerOverrides = providerId
                ? overrideList
                : overrideList.filter((o: any) => o.provider_id === provider.id);
            const availWithOverrides = { ...availability, schedule_overrides: providerOverrides };

            const appointments = await apiService.getAppointmentsPublic({
                provider_id: provider.id,
                organization_id: form.organization_id,
                date_range: { start: rangeStart, end: rangeEnd }
            });

            for (const dateStr of days) {
                const slots = calculateAvailableSlots(
                    availWithOverrides,
                    appointments,
                    dateStr,
                    duration,
                    provider.id,
                    selectedType?.id
                );
                if (slots.length > 0) return true;
            }
        }
        return false;
    };

    const loadOrganizationSettings = async () => {
        try {
            const org = await apiService.getOrganizationPublic(form.organization_id);
            setOrgSettings(org.settings || {});
        } catch (err) {
            console.error('Error loading organization settings:', err);
            setOrgSettings({
                scheduling_mode: 'require_approval',
                default_duration_minutes: 30,
                advance_booking_days: 90,
                minimum_notice_hours: 24
            });
        } finally {
            setOrgSettingsLoaded(true);
        }
    };

    const loadProviders = async () => {
        try {
            const users = await apiService.getOrganizationUsersPublic(form.organization_id);
            setProviders(Array.isArray(users) ? users.filter((u: any) => u.is_provider) : []);
        } catch (err) {
            console.error('Error loading providers:', err);
        }
    };

    const loadAppointmentTypes = async () => {
        try {
            const types = await apiService.getAppointmentTypesPublic(form.organization_id, { locationId: locationId || undefined });
            let filteredTypes = types;
            if (bookingContext === 'online') {
                filteredTypes = types.filter((t: AppointmentType) => t.allow_online_booking);
            } else if (bookingContext === 'portal') {
                filteredTypes = types.filter((t: AppointmentType) => t.allow_portal_booking);
            }
            setAppointmentTypes(filteredTypes);

            if (filteredTypes.length > 0 && !selectedType) {
                setSelectedType(filteredTypes[0]);
            }
        } catch (err) {
            console.error('Error loading appointment types:', err);
        }
    };

    const loadAvailableSlots = async () => {
        if (!selectedDate) return;

        setLoading(true);
        setError(null);

        try {
            const dateStr = selectedDate.toISOString().split('T')[0];
            const endDate = new Date(selectedDate);
            endDate.setDate(endDate.getDate() + 1);
            const endDateStr = endDate.toISOString().split('T')[0];

            const duration = selectedType?.duration_minutes ||
                orgSettings.default_duration_minutes ||
                30;

            let allSlots: ProviderSlot[] = [];

            if (providerId) {
                const [providerAvailability, overrides, appointments] = await Promise.all([
                    apiService.getProviderAvailabilityPublic(providerId, { locationId: locationId || undefined, organizationId: form.organization_id }),
                    apiService.getScheduleOverridesPublic(providerId, dateStr, endDateStr, { organizationId: form.organization_id }),
                    // Conflict check is deliberately org-wide (no location filter):
                    // a provider booked at one site is unavailable at every site.
                    apiService.getAppointmentsPublic({
                        provider_id: providerId,
                        organization_id: form.organization_id,
                        date_range: { start: dateStr, end: endDateStr }
                    })
                ]);

                // With location filtering (or a single-location org) at most one
                // availability record comes back per provider; never union sites.
                const availList = Array.isArray(providerAvailability) ? providerAvailability : [providerAvailability];
                const availWithOverrides = { ...(availList[0] || {}), schedule_overrides: overrides || [] };

                const slots = calculateAvailableSlots(
                    availWithOverrides,
                    appointments,
                    dateStr,
                    duration,
                    providerId,
                    selectedType?.id
                );

                const provider = providers.find(p => p.id === providerId) ||
                    (providerName ? { first_name: providerName.split(' ')[0], last_name: providerName.split(' ').slice(1).join(' ') } : await apiService.getUserPublic(providerId));

                allSlots = slots.map(slot => ({
                    ...slot,
                    provider_name: `${provider.first_name} ${provider.last_name}${provider.credentials ? `, ${provider.credentials}` : ''}`
                }));
            } else {
                let providerList = providers.length > 0 ? providers :
                    (await apiService.getOrganizationUsersPublic(form.organization_id));
                providerList = Array.isArray(providerList) ? providerList.filter((u: any) => u.is_provider) : [];

                if (providerIds && providerIds.length > 0) {
                    providerList = providerList.filter((p: any) => providerIds.includes(p.id));
                }

                if (selectedType?.provider_ids?.length) {
                    providerList = providerList.filter((p: any) => selectedType.provider_ids!.includes(p.id));
                }

                if (providerList.length === 0) {
                    setAvailableSlots([]);
                    setError('No available times for this date. Please select another date.');
                    setLoading(false);
                    return;
                }

                // Batch fetch all provider availability and overrides
                const providerIdCsv = providerList.map((p: any) => p.id).join(',');
                const [allAvailability, allOverrides] = await Promise.all([
                    apiService.getProviderAvailabilityPublic(providerIdCsv, { locationId: locationId || undefined, organizationId: form.organization_id }),
                    apiService.getScheduleOverridesPublic(providerIdCsv, dateStr, endDateStr, { organizationId: form.organization_id })
                ]);
                const availabilityList = Array.isArray(allAvailability) ? allAvailability : [allAvailability];
                const overrideList = Array.isArray(allOverrides) ? allOverrides : [];

                // Track per-provider appointment counts in this window so we can round-robin
                // when condensing duplicate-time slots.
                const appointmentCountsByProvider: Record<string, number> = {};

                for (const provider of providerList) {
                    try {
                        const availability = availabilityList.find((a: any) => a.provider_id === provider.id);
                        if (!availability || !availability.is_active) continue;

                        const providerOverrides = overrideList.filter((o: any) => o.provider_id === provider.id);
                        const availWithOverrides = { ...availability, schedule_overrides: providerOverrides };

                        const appointments = await apiService.getAppointmentsPublic({
                            provider_id: provider.id,
                            organization_id: form.organization_id,
                            date_range: { start: dateStr, end: endDateStr }
                        });

                        appointmentCountsByProvider[provider.id] = Array.isArray(appointments) ? appointments.length : 0;

                        const providerSlots = calculateAvailableSlots(
                            availWithOverrides,
                            appointments,
                            dateStr,
                            duration,
                            provider.id,
                            selectedType?.id
                        );

                        const namedSlots = providerSlots.map(slot => ({
                            ...slot,
                            provider_name: `${provider.first_name} ${provider.last_name}${provider.credentials ? `, ${provider.credentials}` : ''}`
                        }));

                        allSlots = [...allSlots, ...namedSlots];
                    } catch (err) {
                        console.error(`Error loading slots for provider ${provider.id}:`, err);
                    }
                }

                if (!showProviderName) {
                    allSlots = condenseSlotsRoundRobin(allSlots, appointmentCountsByProvider);
                }

                allSlots.sort((a, b) => a.start_time.localeCompare(b.start_time));
            }

            setAvailableSlots(allSlots);
            lastSlotsLoadedAtRef.current = Date.now();

            if (allSlots.length === 0) {
                setError('No available times for this date. Please select another date.');
            }
        } catch (err) {
            console.error('Error loading slots:', err);
            setError('Failed to load available times. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const calculateAvailableSlots = (
        availability: any,
        appointments: any[],
        dateStr: string,
        duration: number,
        providerId: string,
        appointmentTypeId?: string
    ): ProviderSlot[] => {
        const slots: ProviderSlot[] = [];
        // Weekly pattern in force on this date (scheduled changes + history) with
        // the template's start/end bounds applied; null = closed unless an
        // 'open' override adds hours.
        const baseSchedule = resolveDaySchedule(availability, dateStr);

        // Get ALL overrides for this date (supports multiple per day)
        const dateOverrides = (availability.schedule_overrides || []).filter(
            (o: any) => o.date === dateStr
        );

        // Company holiday (org-wide closure): no slots, same rule as the in-app engine
        if (buildHolidaySet(orgSettings).has(dateStr)) return slots;

        // Check for all-day block. A blockless calendar note also blocks the whole
        // day when the org has self_service_notes_block_online on.
        const notesBlock = orgSettings.self_service_notes_block_online === true;
        const dayBlocked = dateOverrides.some(
            (o: any) => (o.type === 'unavailable' || (notesBlock && o.type === 'note')) && (!o.blocks || o.blocks.length === 0)
        );
        if (dayBlocked) return slots;

        // Start with base template blocks
        let effectiveBlocks: any[] = (baseSchedule?.is_available && baseSchedule?.blocks)
            ? [...baseSchedule.blocks] : [];

        // If day is normally closed, only proceed if there are 'open' overrides
        if (!baseSchedule || !baseSchedule.is_available) {
            const hasOpen = dateOverrides.some((o: any) => o.type === 'open' && o.blocks);
            if (!hasOpen) return slots;
            effectiveBlocks = [];
        }

        // Apply open overrides (additive)
        for (const ov of dateOverrides) {
            if (ov.type === 'open' && ov.blocks) {
                effectiveBlocks.push(...ov.blocks);
            }
        }

        // Apply custom overrides (replacement)
        for (const ov of dateOverrides) {
            if (ov.type === 'custom' && ov.blocks) {
                effectiveBlocks = [...ov.blocks];
            }
        }

        // Subtract unavailable overrides with specific blocks (and timed calendar
        // notes when self_service_notes_block_online is on)
        for (const ov of dateOverrides) {
            if ((ov.type === 'unavailable' || (notesBlock && ov.type === 'note')) && ov.blocks?.length > 0) {
                const filtered: any[] = [];
                for (const block of effectiveBlocks) {
                    let remaining = [block];
                    for (const ub of ov.blocks) {
                        remaining = remaining.flatMap((r: any) => {
                            const rStart = parseTime(r.start_time);
                            const rEnd = parseTime(r.end_time);
                            const uStart = parseTime(ub.start_time);
                            const uEnd = parseTime(ub.end_time);
                            if (rStart >= uEnd || rEnd <= uStart) return [r];
                            const parts: any[] = [];
                            if (rStart < uStart) parts.push({ start_time: r.start_time, end_time: ub.start_time });
                            if (rEnd > uEnd) parts.push({ start_time: ub.end_time, end_time: r.end_time });
                            return parts;
                        });
                    }
                    filtered.push(...remaining);
                }
                effectiveBlocks = filtered;
            }
        }

        effectiveBlocks.forEach((block: any) => {
            if (block.appointment_types?.length > 0 && appointmentTypeId) {
                if (!block.appointment_types.includes(appointmentTypeId)) return;
            }

            const startTime = parseTime(block.start_time);
            const endTime = parseTime(block.end_time);

            for (let time = startTime; time + duration <= endTime; time += duration) {
                const slotStart = formatTime(time);
                const slotEnd = formatTime(time + duration);

                const excludedStatuses = ['cancelled', 'declined', 'no_show'];
                const isBooked = appointments.some(appt => {
                    if (excludedStatuses.includes(appt.status)) return false;
                    return (
                        appt.appointment_date === dateStr &&
                        timeOverlaps(slotStart, slotEnd, appt.start_time, appt.end_time)
                    );
                });

                if (!isBooked) {
                    slots.push({
                        provider_id: providerId,
                        provider_name: '',
                        date: dateStr,
                        start_time: slotStart,
                        end_time: slotEnd,
                        available: true
                    });
                }
            }
        });

        return slots;
    };

    const parseTime = (timeStr: string): number => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
    };

    const formatTime = (minutes: number): string => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    const timeOverlaps = (
        start1: string, end1: string,
        start2: string, end2: string
    ): boolean => {
        return start1 < end2 && end1 > start2;
    };

    const createPreferredSlot = (slot: ProviderSlot): PreferredSlot => {
        const startDateTime = new Date(`${slot.date}T${slot.start_time}:00`);
        const endDateTime = new Date(`${slot.date}T${slot.end_time}:00`);

        return {
            appointment_date: slot.date,
            start_time: slot.start_time,
            end_time: slot.end_time,
            provider_id: slot.provider_id,
            provider_name: slot.provider_name,
            location_id: locationId || undefined,
            start_timestamp: startDateTime.getTime(),
            end_timestamp: endDateTime.getTime()
        };
    };

    const isSlotAlreadySelected = (slot: ProviderSlot): boolean => {
        return selectedSlots.some(
            s => s.appointment_date === slot.date &&
                s.start_time === slot.start_time &&
                s.provider_id === slot.provider_id
        );
    };

    const handleSlotSelection = (slot: ProviderSlot) => {
        const userTimezone = detectTimezone();
        const duration = selectedType?.duration_minutes || orgSettings.default_duration_minutes || 30;

        if (isMultiSlotMode) {
            // Multi-slot mode for require_approval orgs
            if (isSlotAlreadySelected(slot)) {
                // Remove if already selected
                handleRemoveSlot(slot.date, slot.start_time, slot.provider_id);
                return;
            }

            if (selectedSlots.length >= MAX_PREFERRED_SLOTS) {
                setError(`You can select up to ${MAX_PREFERRED_SLOTS} preferred times. Remove one to add another.`);
                return;
            }

            setError(null);
            const newSlot = createPreferredSlot(slot);
            const updatedSlots = [...selectedSlots, newSlot];
            setSelectedSlots(updatedSlots);

            // Update parent with multi-slot data
            const appointmentData: AppointmentTimeData = {
                preferred_slots: updatedSlots,
                appointment_type: selectedType?.name,
                appointment_type_id: selectedType?.id,
                duration_minutes: duration,
                is_flexible: orgSettings.allow_time_adjustment,
                timezone: userTimezone,
                location_id: locationId || undefined,
                is_telehealth: isTelehealth
            };

            onChange(appointmentData);
        } else {
            // Single-slot mode for auto_confirm orgs (existing behavior)
            setSelectedSlot(slot);

            const startDateTime = new Date(`${slot.date}T${slot.start_time}:00`);
            const endDateTime = new Date(`${slot.date}T${slot.end_time}:00`);

            const appointmentData: AppointmentTimeData = {
                provider_id: slot.provider_id,
                provider_name: slot.provider_name,
                location_id: locationId || undefined,
                appointment_date: slot.date,
                start_time: slot.start_time,
                end_time: slot.end_time,
                appointment_type: selectedType?.name,
                appointment_type_id: selectedType?.id,
                duration_minutes: duration,
                is_flexible: orgSettings.allow_time_adjustment,
                timezone: userTimezone,
                start_timestamp: startDateTime.getTime(),
                end_timestamp: endDateTime.getTime(),
                is_telehealth: isTelehealth
            };

            onChange(appointmentData);
        }
    };

    const handleRemoveSlot = (date: string, startTime: string, providerId: string) => {
        const userTimezone = detectTimezone();
        const duration = selectedType?.duration_minutes || orgSettings.default_duration_minutes || 30;

        const updatedSlots = selectedSlots.filter(
            s => !(s.appointment_date === date && s.start_time === startTime && s.provider_id === providerId)
        );
        setSelectedSlots(updatedSlots);
        setError(null);

        if (updatedSlots.length === 0) {
            onChange(null as any);
        } else {
            const appointmentData: AppointmentTimeData = {
                preferred_slots: updatedSlots,
                appointment_type: selectedType?.name,
                appointment_type_id: selectedType?.id,
                duration_minutes: duration,
                is_flexible: orgSettings.allow_time_adjustment,
                timezone: userTimezone,
                location_id: locationId || undefined,
                is_telehealth: isTelehealth
            };

            onChange(appointmentData);
        }
    };

    const formatDisplayTime = (time: string) => {
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    const formatDisplayDate = (dateStr: string) => {
        const date = new Date(dateStr + 'T12:00:00');
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    const getDaysInMonth = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay());

        const days: DayInfo[] = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const minDate = new Date();
        minDate.setHours(minDate.getHours() + (orgSettings.minimum_notice_hours || 24));
        minDate.setHours(0, 0, 0, 0);

        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + (orgSettings.advance_booking_days || 90));
        maxDate.setHours(0, 0, 0, 0);

        for (let d = new Date(startDate); d <= lastDay || d.getDay() !== 0; d.setDate(d.getDate() + 1)) {
            const isCurrentMonth = d.getMonth() === month;
            const isPast = d < minDate;
            const isTooFar = d > maxDate;
            const isToday = d.toDateString() === today.toDateString();

            days.push({
                date: new Date(d),
                isCurrentMonth,
                isPast,
                isTooFar,
                isToday,
                isSelected: selectedDate && d.toDateString() === selectedDate.toDateString(),
                isDisabled: isPast || isTooFar || !isCurrentMonth
            });
        }

        return days;
    };

    const navigateMonth = (direction: number) => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() + direction);
        setCurrentMonth(newMonth);
    };

    if (awaitingLocation) {
        return (
            <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                    <p className="text-blue-800 text-sm">
                        Please select a location to see available times.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Scheduling Mode Alert */}
            {isMultiSlotMode && (
                <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                    <div className="flex items-start gap-2">
                        <Info className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                            <p className="text-amber-800 font-medium">Appointment Request</p>
                            <p className="text-amber-700 text-sm mt-1">
                                Please select up to {MAX_PREFERRED_SLOTS} preferred appointment times.
                                The practice will review your request and confirm one of your selected times.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Selected Slots Summary (Multi-slot mode) */}
            {isMultiSlotMode && selectedSlots.length > 0 && (
                <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-blue-800 font-medium">
                            Selected Times ({selectedSlots.length}/{MAX_PREFERRED_SLOTS})
                        </p>
                    </div>
                    <div className="space-y-2">
                        {selectedSlots.map((slot, idx) => (
                            <div
                                key={`${slot.appointment_date}-${slot.start_time}-${slot.provider_id}`}
                                className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-blue-200"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                                        {idx + 1}
                                    </span>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">
                                            {formatDisplayDate(slot.appointment_date)} at {formatDisplayTime(slot.start_time)}
                                        </p>
                                        {slot.provider_name && (
                                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                                <User className="w-3 h-3" />
                                                {slot.provider_name}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveSlot(slot.appointment_date, slot.start_time, slot.provider_id)}
                                    disabled={disabled}
                                    className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-600 transition-colors"
                                    aria-label="Remove time slot"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                    {selectedSlots.length < MAX_PREFERRED_SLOTS && (
                        <p className="text-blue-600 text-xs mt-2">
                            You can add {MAX_PREFERRED_SLOTS - selectedSlots.length} more preferred time{MAX_PREFERRED_SLOTS - selectedSlots.length !== 1 ? 's' : ''}.
                        </p>
                    )}
                </div>
            )}

            {/* Appointment Type Selection */}
            {showAppointmentTypes && appointmentTypes.length > 0 && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Appointment Type
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {appointmentTypes.map(type => (
                            <button
                                key={type.id}
                                type="button"
                                onClick={() => setSelectedType(type)}
                                disabled={disabled}
                                className={`p-3 rounded-lg border text-left transition-colors ${selectedType?.id === type.id
                                    ? 'border-[#0facd7] bg-blue-50'
                                    : 'border-gray-300 hover:border-gray-400'
                                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <div className="font-medium">{type.name}</div>
                                <div className="text-sm text-gray-500 mt-1">
                                    {type.duration_minutes} minutes
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Telehealth Toggle (accepted) */}
            {selectedType?.telehealth_status === 'accepted' && (
                <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                    <label className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-2">
                            <Video className="w-5 h-5 text-blue-600" />
                            <span className="font-medium text-blue-900">This will be a video visit</span>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={isTelehealth}
                            onClick={() => setIsTelehealth(!isTelehealth)}
                            disabled={disabled}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isTelehealth ? 'bg-blue-600' : 'bg-gray-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isTelehealth ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                    </label>
                    {isTelehealth && (
                        <p className="text-sm text-blue-700 mt-2">You'll receive a video link after booking.</p>
                    )}
                </div>
            )}

            {/* Telehealth Info (required) */}
            {selectedType?.telehealth_status === 'required' && (
                <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                    <div className="flex items-center gap-2">
                        <Video className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-900">This is a telehealth visit</span>
                    </div>
                    <p className="text-sm text-blue-700 mt-1">You'll receive a video link after booking.</p>
                </div>
            )}

            {/* Look-ahead notice (scanAheadEnabled) */}
            {scanAheadEnabled && noAvailabilityAhead && (
                <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                    <div className="flex items-start gap-2">
                        <Info className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-amber-800 font-medium">No open times in the next two weeks</p>
                            <p className="text-amber-700 text-sm mt-1">
                                Nothing is open for these selections through the end of next week.
                                {onJoinWaitlist
                                    ? ' Try another location or provider, call the office, or join the waitlist and we will contact you when a time opens.'
                                    : ' Try another location or provider, or call the office.'}
                            </p>
                            {onJoinWaitlist && (
                                <button
                                    type="button"
                                    onClick={onJoinWaitlist}
                                    disabled={disabled}
                                    className="mt-3 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Join the waitlist
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Calendar */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Date
                </label>
                <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                        <button
                            type="button"
                            onClick={() => navigateMonth(-1)}
                            className="p-1 hover:bg-gray-100 rounded"
                            disabled={disabled}
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <h3 className="font-medium">
                            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h3>
                        <button
                            type="button"
                            onClick={() => navigateMonth(1)}
                            className="p-1 hover:bg-gray-100 rounded"
                            disabled={disabled}
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="text-center text-xs font-medium text-gray-600 py-2">
                                {day}
                            </div>
                        ))}

                        {getDaysInMonth().map((day, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={() => !day.isDisabled && setSelectedDate(day.date)}
                                disabled={day.isDisabled || disabled}
                                className={`
                                    p-2 text-sm rounded-lg transition-colors
                                    ${day.isCurrentMonth ? '' : 'text-gray-400'}
                                    ${day.isToday ? 'font-bold' : ''}
                                    ${day.isSelected ? 'bg-[#0facd7] text-white' : ''}
                                    ${!day.isDisabled && !day.isSelected ? 'hover:bg-gray-100' : ''}
                                    ${day.isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                `}
                            >
                                {day.date.getDate()}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Time Slots */}
            {selectedDate && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Clock className="inline w-4 h-4 mr-1" />
                        Available Times
                        {isMultiSlotMode && selectedSlots.length >= MAX_PREFERRED_SLOTS && (
                            <span className="text-amber-600 font-normal ml-2">
                                (Maximum selections reached)
                            </span>
                        )}
                    </label>

                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-[#0facd7]" />
                            <span className="ml-2">Loading available times...</span>
                        </div>
                    ) : error ? (
                        <div className="p-4 border rounded-lg bg-red-50 border-red-200">
                            <p className="text-red-700 text-sm">{error}</p>
                        </div>
                    ) : availableSlots.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {availableSlots.map((slot, idx) => {
                                const isSelected = isMultiSlotMode
                                    ? isSlotAlreadySelected(slot)
                                    : selectedSlot?.start_time === slot.start_time &&
                                      selectedSlot?.provider_id === slot.provider_id;

                                const isDisabledByMax = isMultiSlotMode &&
                                    selectedSlots.length >= MAX_PREFERRED_SLOTS &&
                                    !isSlotAlreadySelected(slot);

                                return (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => handleSlotSelection(slot)}
                                        disabled={disabled || isDisabledByMax}
                                        className={`
                                            p-3 rounded-lg border text-center transition-colors relative
                                            ${isSelected
                                                ? 'border-[#0facd7] bg-blue-50 ring-2 ring-[#0facd7]'
                                                : 'border-gray-300 hover:border-gray-400'
                                            }
                                            ${disabled || isDisabledByMax ? 'opacity-50 cursor-not-allowed' : ''}
                                        `}
                                    >
                                        {isSelected && isMultiSlotMode && (
                                            <span className="absolute -top-2 -right-2 flex items-center justify-center w-5 h-5 rounded-full bg-[#0facd7] text-white text-xs font-medium">
                                                {selectedSlots.findIndex(
                                                    s => s.appointment_date === slot.date &&
                                                        s.start_time === slot.start_time &&
                                                        s.provider_id === slot.provider_id
                                                ) + 1}
                                            </span>
                                        )}
                                        <div className="font-medium">
                                            {formatDisplayTime(slot.start_time)}
                                        </div>
                                        {!providerId && showProviderName && (
                                            <div className="text-xs text-gray-500 mt-1 flex items-center justify-center">
                                                <User className="w-3 h-3 mr-1" />
                                                {slot.provider_name}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="p-4 border rounded-lg bg-gray-50">
                            <p className="text-gray-600 text-sm">
                                No available times for this date. Please select another date.
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Selected Summary (Single-slot mode only) */}
            {!isMultiSlotMode && selectedSlot && (
                <div className="p-4 border rounded-lg bg-green-50 border-green-200">
                    <p className="text-green-800 font-medium mb-1">Selected Appointment:</p>
                    <p className="text-green-700 text-sm">
                        {selectedDate?.toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })} at {formatDisplayTime(selectedSlot.start_time)}
                        {!providerId && showProviderName && (
                            <span> with {selectedSlot.provider_name}</span>
                        )}
                    </p>
                </div>
            )}

            {/* Final Summary (Multi-slot mode) */}
            {isMultiSlotMode && selectedSlots.length > 0 && (
                <div className="p-4 border rounded-lg bg-green-50 border-green-200">
                    <p className="text-green-800 font-medium mb-1">
                        Ready to Submit Request
                    </p>
                    <p className="text-green-700 text-sm">
                        You've selected {selectedSlots.length} preferred time{selectedSlots.length !== 1 ? 's' : ''}.
                        The practice will confirm one of these times with you.
                    </p>
                </div>
            )}
        </div>
    );
};

export default AppointmentTimeField;
